import React, { createContext, useContext, useEffect, useMemo } from 'react';
import type { Locale as AntdLocale } from 'antd/es/locale';
import enUS from 'antd/es/locale/en_US';
import esES from 'antd/es/locale/es_ES';
import frFR from 'antd/es/locale/fr_FR';
import jaJP from 'antd/es/locale/ja_JP';
import ruRU from 'antd/es/locale/ru_RU';
import zhCN from 'antd/es/locale/zh_CN';
import zhHK from 'antd/es/locale/zh_HK';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/ja';
import 'dayjs/locale/ru';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-hk';
import { useI18n } from './useI18n';
import type { LocaleId } from './types';

const ANTD_LOCALES: Record<LocaleId, AntdLocale> = {
  zh_CN: zhCN,
  zh_HK: zhHK,
  en_US: enUS,
  ru_RU: ruRU,
  es_ES: esES,
  fr_FR: frFR,
  ja_JP: jaJP,
};

const DAYJS_LOCALES: Record<LocaleId, string> = {
  zh_CN: 'zh-cn',
  zh_HK: 'zh-hk',
  en_US: 'en',
  ru_RU: 'ru',
  es_ES: 'es',
  fr_FR: 'fr',
  ja_JP: 'ja',
};

const DOCUMENT_LANGS: Record<LocaleId, string> = {
  zh_CN: 'zh-CN',
  zh_HK: 'zh-HK',
  en_US: 'en-US',
  ru_RU: 'ru-RU',
  es_ES: 'es-ES',
  fr_FR: 'fr-FR',
  ja_JP: 'ja-JP',
};

interface I18nContextValue {
  locale: LocaleId;
  antdLocale: AntdLocale;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { locale } = useI18n();

  useEffect(() => {
    dayjs.locale(DAYJS_LOCALES[locale]);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = DOCUMENT_LANGS[locale];
    }
  }, [locale]);

  const value = useMemo<I18nContextValue>(() => ({
    locale,
    antdLocale: ANTD_LOCALES[locale],
  }), [locale]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

export function useI18nContext(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18nContext must be used within I18nProvider');
  }
  return context;
}

export default I18nProvider;
